<template>
  <!-- Discord Banner -->
  <div class="bg-indigo-900 bg-opacity-80 px-4 py-1 text-center text-white text-sm transition-all duration-200">
    <a href="https://discord.gg/UA9pNFWwCe" target="_blank" class="hover:underline flex justify-center items-center gap-2 text-indigo-200 hover:text-indigo-300">
      <i class="fab fa-discord text-lg text-indigo-400"></i>
      เข้าร่วมดิสคอร์ดเพื่อพูดคุย แลกเปลี่ยน ซื้อขาย และอ่านวิธีการเล่นจากผู้เล่นอื่น
    </a>
  </div>
  <div class="block sm:flex justify-between items-center bg-gray-900 bg-opacity-80 px-4 py-2 text-white border-b border-gray-700">
    <div class="flex justify-between space-x-1 text-xs sm:text-sm w-full">
      <div class="text-sm sm:text-base font-semibold text-indigo-200 flex items-center gap-2">
        <i class="fas fa-comments text-indigo-400 text-lg"></i>
        <span class="hidden sm:block">แชทผู้เล่น</span>
      </div>
      <div class="flex items-center space-x-1">
        <button @click="chatRoom = 'general'"
          class="py-1 px-2 rounded-lg transition-all duration-200 flex items-center gap-2 relative"
          :class="chatRoom === 'general'
            ? 'bg-indigo-600 hover:bg-indigo-500 border border-indigo-700 text-white'
            : 'bg-gray-700 hover:bg-gray-600 border border-gray-600 text-gray-300'">
          <i class="fas fa-globe text-sm"></i>
          <span class="hidden sm:block">ห้องทั่วไป</span>
          <span class="sm:hidden">ทั่วไป</span>
          <span class="tooltip">ห้องทั่วไป</span>
        </button>
        <button @click="toggleStorage"
          class="py-1 px-2 bg-indigo-600 hover:bg-indigo-500 border border-indigo-700 rounded-lg transition-all duration-200 flex items-center gap-2 relative"
          :class="{ 'opacity-60 cursor-not-allowed': loading }" :disabled="loading">
          <i class="fas fa-warehouse text-sm"></i>
          <div class="hidden sm:block">ห้องเก็บของ</div>
          <span class="tooltip">ห้องเก็บของ</span>
        </button>
      </div>
    </div>
  </div>

  <!-- Chat Input -->
  <div class="bg-gray-900 bg-opacity-70 p-1 flex items-center border-b border-gray-800">
    <input v-if="!isBannedFromChat" v-model="newMessage" type="text" placeholder="พิมพ์ข้อความ..."
      class="flex-1 bg-gray-700 text-white rounded-lg p-2 px-4 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-200"
      @keyup.enter="sendMessage" />
    <div v-if="isBannedFromChat" class="flex-1 text-red-400 text-sm p-2">คุณถูกห้ามใช้งานแชท</div>
    <button v-if="!isBannedFromChat" @click="sendMessage"
      class="ml-2 p-1 px-4 bg-indigo-600 hover:bg-indigo-500 border border-indigo-700 text-white rounded-lg transition-all duration-200 flex items-center gap-2 relative"
      :disabled="sending || isBannedFromChat">
      <i v-if="!sending" class="fas fa-paper-plane text-sm"></i>
      <i v-else class="fas fa-spinner animate-spin text-sm"></i>
      ส่ง
      <span class="tooltip">ส่งข้อความ</span>
    </button>
  </div>

  <!-- Chat Body -->
  <div class="p-3 pb-24 bg mb-auto h-full overflow-auto bg-gradient-to-b from-gray-800 text-white scrollbar-thin" id="style-1">
    <div v-if="loading" class="flex items-center justify-center py-6">
      <i class="fas fa-spinner animate-spin text-indigo-200 text-4xl"></i>
    </div>
    <div v-else-if="error" class="text-center text-red-300 py-6 text-sm">{{ error }}</div>
    <div v-else class="space-y-3">
      <div v-for="msg in filteredMessages" :key="msg.id" class="animate-fadeIn">

        <!-- ╔═══════════════════════════════════════════════════╗
             ║              KILL FEED CARD                       ║
             ╚═══════════════════════════════════════════════════╝ -->
        <div v-if="msg.type === 'killFeed'" class="kill-feed-wrapper">
          <div :class="['kill-feed-card', killFeedTheme(msg.cause).card]">

            <!-- scanline sweep -->
            <div class="kill-feed-scanline"></div>

            <!-- Top ribbon -->
            <div :class="['kill-feed-ribbon', killFeedTheme(msg.cause).ribbon]">
              <span class="kill-feed-ribbon-icon">{{ killFeedTheme(msg.cause).icon }}</span>
              <span class="kill-feed-ribbon-label">{{ killFeedTheme(msg.cause).label }}</span>
              <span class="kill-feed-ribbon-time ml-auto">{{ formatTimestamp(msg.timestamp) }}</span>
            </div>

            <!-- ─── TRAP / TRAP-ASSASSINATION with owner: 3-column layout ── -->
            <template v-if="(msg.cause === 'trap' || msg.cause === 'trapAssassination') && msg.trapOwnerUid">

              <!-- row: victim ← trap ← trap owner -->
              <div class="kill-feed-triple">

                <!-- Victim (ผู้โจมตีที่ตาย) -->
                <div class="kill-feed-combatant">
                  <div class="kill-feed-avatar-wrap victim">
                    <img :src="getKillFeedChar(msg.victimUid)?.imageUrl || 'default-avatar.png'"
                      class="kill-feed-avatar victim-avatar" @error="handleImageError" />
                    <span class="kill-feed-avatar-badge victim-badge">💀</span>
                  </div>
                  <div class="kill-feed-name victim-name">
                    {{ getKillFeedChar(msg.victimUid)?.characterName || '...' }}
                  </div>
                  <div class="kill-feed-role victim-role">ผู้โจมตี</div>
                </div>

                <!-- Centre: trap icon + name -->
                <div class="kill-feed-vs-col">
                  <div :class="['kill-feed-vs-icon', killFeedTheme(msg.cause).vsIcon]">⚡</div>
                  <div v-if="msg.trapName" class="kill-feed-weapon">{{ msg.trapName }}</div>
                </div>

                <!-- Trap Owner (ได้รับลูกเต๋าโบนัส) -->
                <div class="kill-feed-combatant">
                  <div class="kill-feed-avatar-wrap trap-owner">
                    <img :src="getKillFeedChar(msg.trapOwnerUid)?.imageUrl || 'default-avatar.png'"
                      class="kill-feed-avatar trap-owner-avatar" @error="handleImageError" />
                    <span class="kill-feed-avatar-badge trap-owner-badge">🎲</span>
                  </div>
                  <div class="kill-feed-name trap-owner-name">
                    {{ getKillFeedChar(msg.trapOwnerUid)?.characterName || '...' }}
                  </div>
                  <div class="kill-feed-role trap-owner-role">เจ้าของกับดัก</div>
                  <!-- bonus dice badge -->
                  <div class="trap-dice-bonus">
                    <span class="trap-dice-bonus-icon">🎲</span>
                    <span class="trap-dice-bonus-text">+1 ลูกเต๋า</span>
                  </div>
                </div>

              </div>

            </template>

            <!-- ─── All other causes: standard 2-column layout ──── -->
            <template v-else>
              <div class="kill-feed-combat">
                <!-- Killer -->
                <div class="kill-feed-combatant">
                  <div class="kill-feed-avatar-wrap killer">
                    <img :src="getKillFeedChar(msg.killerUid)?.imageUrl || 'default-avatar.png'"
                      class="kill-feed-avatar" @error="handleImageError" />
                    <span class="kill-feed-avatar-badge killer-badge">⚔️</span>
                  </div>
                  <div class="kill-feed-name killer-name">
                    {{ getKillFeedChar(msg.killerUid)?.characterName || '...' }}
                  </div>
                  <div class="kill-feed-role killer-role">ผู้ล่า</div>
                </div>
                <!-- VS -->
                <div class="kill-feed-vs-col">
                  <div :class="['kill-feed-vs-icon', killFeedTheme(msg.cause).vsIcon]">
                    {{ killFeedTheme(msg.cause).vsEmoji }}
                  </div>
                  <div v-if="msg.trapName" class="kill-feed-weapon">{{ msg.trapName }}</div>
                </div>
                <!-- Victim -->
                <div class="kill-feed-combatant">
                  <div class="kill-feed-avatar-wrap victim">
                    <img :src="getKillFeedChar(msg.victimUid)?.imageUrl || 'default-avatar.png'"
                      class="kill-feed-avatar victim-avatar" @error="handleImageError" />
                    <span class="kill-feed-avatar-badge victim-badge">💀</span>
                  </div>
                  <div class="kill-feed-name victim-name">
                    {{ getKillFeedChar(msg.victimUid)?.characterName || '...' }}
                  </div>
                  <div class="kill-feed-role victim-role">เหยื่อ</div>
                </div>
              </div>
            </template>

            <!-- Glow bar -->
            <div :class="['kill-feed-glow-bar', killFeedTheme(msg.cause).glowBar]"></div>

            <!-- Admin delete -->
            <button v-if="isAdmin"
              class="absolute top-2 right-2 text-gray-500 hover:text-red-400 text-xs transition-colors duration-150 z-10"
              @click="deleteMessage(msg.id)">
              <i class="fas fa-trash"></i>
            </button>
          </div>
        </div>

        <!-- ╔═══════════════════════════════════════════════════╗
             ║              NORMAL MESSAGE                       ║
             ╚═══════════════════════════════════════════════════╝ -->
        <div v-else class="flex items-start space-x-3"
          :class="{ 'flex-row-reverse': msg.senderUID === user.uid }">
          <img :src="msg.senderImage || 'default-avatar.png'" alt="Avatar"
            class="w-10 h-10 rounded-full border-2 border-gray-600 flex-shrink-0" @error="handleImageError" />
          <div class="bg-gray-900 bg-opacity-80 p-3 rounded-lg border border-gray-800 max-w-xs sm:max-w-md transition-all duration-200 hover:shadow-md"
            :class="{ 'bg-indigo-900 bg-opacity-80 border-indigo-700': msg.senderUID === user.uid }">
            <div class="flex justify-between items-center mb-1">
              <p class="text-sm font-semibold text-indigo-200">{{ msg.senderName }}</p>
              <div class="flex items-center gap-2">
                <span class="text-xs text-gray-400">{{ formatTimestamp(msg.timestamp) }}</span>
                <i v-if="msg.senderUID === user.uid || isAdmin"
                  class="fas fa-trash text-red-400 cursor-pointer text-xs hover:text-red-300 relative"
                  @click="deleteMessage(msg.id)"><span class="tooltip">ลบข้อความ</span></i>
                <i v-if="!isBannedFromChat"
                  class="fas fa-quote-left text-blue-400 cursor-pointer text-xs hover:text-blue-300 relative"
                  @click="quoteMessage(msg)"><span class="tooltip">อ้างอิงข้อความ</span></i>
                <i v-else class="fas fa-quote-left text-gray-500 cursor-not-allowed text-xs relative">
                  <span class="tooltip">คุณถูกห้ามใช้งานแชท</span></i>
              </div>
            </div>
            <p class="text-sm text-white">
              <span v-for="(part, index) in parseMessage(msg.message)" :key="index">
                <span v-if="part.type === 'text'">{{ part.content }}</span>
                <span v-else-if="part.type === 'item' && itemCache[part.id] && !itemCache[part.id].error && !itemCache[part.id].loading"
                  class="inline-flex items-center cursor-pointer text-blue-400" @click="openItemModal(part.id)">
                  <img :src="itemCache[part.id].img" class="w-5 h-5 mr-1 rounded" @error="handleImageError" />
                  {{ itemCache[part.id].name }}
                </span>
                <span v-else-if="part.type === 'item' && itemCache[part.id] && itemCache[part.id].error" class="text-red-400">ไอเทมถูกลบไปแล้ว</span>
                <span v-else-if="part.type === 'item'" class="inline-flex items-center text-gray-400">
                  <i class="fas fa-spinner animate-spin mr-1"></i>[item: {{ part.id }}]
                </span>
                <div v-else-if="part.type === 'quote'" class="bg-gray-700 p-2 rounded mb-2">
                  <span v-if="quoteCache[part.id] && !quoteCache[part.id].error && !quoteCache[part.id].loading">
                    <p class="text-xs text-gray-300">อ้างอิงจาก {{ quoteCache[part.id].senderName }}:</p>
                    <p class="text-sm">{{ formatQuoteMessage(quoteCache[part.id].message) }}</p>
                  </span>
                  <span v-else-if="quoteCache[part.id] && quoteCache[part.id].error" class="text-red-400">ข้อความอ้างอิงถูกลบไปแล้ว</span>
                  <span v-else class="inline-flex items-center text-gray-400">
                    <i class="fas fa-spinner animate-spin mr-1"></i>โหลดข้อความอ้างอิง...
                  </span>
                </div>
              </span>
            </p>
          </div>
        </div>

      </div><!-- end v-for -->

      <div v-if="hasMoreMessages" class="text-center mt-4 w-full">
        <button @click="loadMore"
          class="py-2 px-4 w-full bg-indigo-600 hover:bg-indigo-500 border border-indigo-700 text-white rounded-lg transition-all duration-200 relative"
          :disabled="loadingMore">
          <span v-if="!loadingMore">โหลดเพิ่ม</span>
          <i v-else class="fas fa-spinner animate-spin"></i>
          <span class="tooltip">โหลดข้อความเพิ่ม</span>
        </button>
      </div>
    </div>
  </div>

  <!-- Storage Modal -->
  <transition name="fade">
    <div v-if="showStorage" class="fixed inset-0 bg-gray-900 bg-opacity-80 flex items-center justify-center z-50">
      <div class="bg-gray-800 p-6 rounded-lg border border-gray-700 shadow-xl w-full max-w-4xl h-2/3 overflow-hidden flex flex-col">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold text-indigo-300 flex items-center gap-2">
            <i v-if="inventorySource === 'equipped'" class="fas fa-user-shield text-indigo-400"></i>
            <i v-else-if="inventorySource === 'bag'" class="fas fa-box text-indigo-400"></i>
            <i v-else class="fas fa-warehouse text-indigo-400"></i>
            {{ inventorySource === 'equipped' ? 'อุปกรณ์สวมใส่' : inventorySource === 'bag' ? 'กระเป๋า' : 'ห้องเก็บของ' }}
          </h3>
          <div class="flex items-center space-x-2">
            <button @click="inventorySource = 'storage'"
              class="py-1 px-3 rounded-lg transition-all duration-200 text-sm flex items-center gap-2"
              :class="inventorySource === 'storage'
                ? 'bg-indigo-600 hover:bg-indigo-500 border border-indigo-700 text-white'
                : 'bg-gray-700 hover:bg-gray-600 border border-gray-600 text-gray-300'">
              <i class="fas fa-warehouse text-sm"></i> คลัง
            </button>
            <button @click="inventorySource = 'bag'"
              class="py-1 px-3 rounded-lg transition-all duration-200 text-sm flex items-center gap-2"
              :class="inventorySource === 'bag'
                ? 'bg-indigo-600 hover:bg-indigo-500 border border-indigo-700 text-white'
                : 'bg-gray-700 hover:bg-gray-600 border border-gray-600 text-gray-300'">
              <i class="fas fa-box text-sm"></i> กระเป๋า
            </button>
            <button @click="inventorySource = 'equipped'"
              class="py-1 px-3 rounded-lg transition-all duration-200 text-sm flex items-center gap-2"
              :class="inventorySource === 'equipped'
                ? 'bg-indigo-600 hover:bg-indigo-500 border border-indigo-700 text-white'
                : 'bg-gray-700 hover:bg-gray-600 border border-gray-600 text-gray-300'">
              <i class="fas fa-user-shield text-sm"></i> สวมใส่
            </button>
          </div>
          <button @click="toggleStorage" class="text-gray-300 hover:text-indigo-300 transition-colors duration-200 focus:outline-none">
            <i class="fas fa-times text-lg"></i>
          </button>
        </div>
        <div class="flex items-center space-x-2 mb-4">
          <select v-model="storageSortPreference" class="bg-gray-700 text-indigo-200 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 border border-gray-600">
            <option value="type">เรียงตามประเภท</option>
            <option value="inventory">เรียงตามลำดับในคลัง</option>
          </select>
          <button @click="toggleTransferFilter"
            class="py-1 px-2 bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded-lg transition-all duration-200 flex items-center relative"
            :class="{ 'bg-indigo-600 hover:bg-indigo-500 border-indigo-700': showTransferOnly }">
            <i class="fa-solid fa-right-left text-sm" :class="{ 'text-green-500': showTransferOnly, 'text-gray-500': !showTransferOnly }"></i>
            <span class="tooltip">แสดงเฉพาะไอเท็มโอนได้</span>
          </button>
          <button @click="openSortModal"
            class="py-1 px-2 bg-indigo-600 hover:bg-indigo-500 border border-indigo-700 rounded-lg transition-all duration-200 flex items-center gap-2 relative"
            :class="{ 'opacity-60 cursor-not-allowed': storageLoading }" :disabled="storageLoading">
            <span v-if="!storageLoading"><i class="fas fa-sort text-sm text-gray-100"></i></span>
            <i v-else class="fas fa-spinner animate-spin text-indigo-200 text-sm"></i>
            <div class="hidden sm:block text-gray-100">จัดเรียง</div>
            <span class="tooltip">จัดเรียง</span>
          </button>
        </div>
        <div class="flex gap-4 mb-4">
          <select v-model="selectedType" class="bg-gray-700 text-white rounded-lg p-2 w-1/3 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-200">
            <option value="">ทุกประเภท</option>
            <option v-for="type in typeOptions" :key="type" :value="type">{{ type }}</option>
          </select>
          <input v-model="searchQuery" type="text" placeholder="ค้นหาไอเทม..."
            class="bg-gray-700 text-white rounded-lg p-2 w-2/3 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all duration-200" />
        </div>
        <div v-if="storageLoading" class="flex items-center justify-center py-6">
          <i class="fas fa-spinner animate-spin text-indigo-200 text-4xl"></i>
        </div>
        <div v-else-if="filteredStorageItems.length > 0" class="space-y-1 overflow-y-auto flex-1 scrollbar-thin" id="storage-scroll">
          <div v-for="item in filteredStorageItems" :key="item.id" @click="selectItem(item)"
            class="flex items-center gap-1 bg-gray-900 p-2 rounded-lg cursor-pointer hover:bg-gray-800 transition-all duration-200 border border-gray-600 animate-fadeIn">
            <img :src="item.img || 'default-item.png'" alt="Item" class="w-8 h-8 rounded-md object-cover border-2"
              :class="item.rarityColors?.borderColor || 'border-indigo-500'" @error="handleImageError" />
            <p class="text-xs sm:text-sm font-semibold" :class="item.rarityColors?.textColor || 'text-white'">
              {{ item.name }}<span v-if="item.level">+{{ item.level }}</span>
            </p>
            <i v-if="item.transfer" class="fa-solid fa-arrow-right-arrow-left text-green-300 text-xs ml-auto"></i>
          </div>
        </div>
        <p v-else class="text-gray-400 text-center py-6 text-sm">
          {{ inventorySource === 'equipped' ? 'ไม่มีอุปกรณ์สวมใส่' : inventorySource === 'bag' ? 'ไม่มีไอเทมในกระเป๋าที่ตรงกับการค้นหา' : 'ไม่มีไอเทมในคลังที่ตรงกับการค้นหา' }}
        </p>
      </div>
    </div>
  </transition>

  <!-- Item Action Modal -->
  <transition name="fade">
    <div v-if="selectedItem" class="fixed inset-0 bg-gray-900 bg-opacity-80 flex items-center justify-center z-50">
      <div class="bg-gray-800 p-6 rounded-lg border border-gray-700 shadow-xl w-full max-w-md">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold text-indigo-300 flex items-center gap-2">
            <i class="fas fa-box-open text-indigo-400"></i> จัดการไอเทม: {{ selectedItem.name }}
          </h3>
          <button @click="selectedItem = null" class="text-gray-300 hover:text-indigo-300 transition-colors duration-200 focus:outline-none">
            <i class="fas fa-times text-lg"></i>
          </button>
        </div>
        <div class="space-y-4">
          <button @click="insertItemToChat"
            class="w-full py-3 px-4 bg-indigo-600 hover:bg-indigo-500 border border-indigo-700 text-white rounded-lg transition-all duration-300 flex items-center justify-center gap-2 shadow-md hover:shadow-xl transform hover:scale-105 relative"
            :disabled="isBannedFromChat">
            <i class="fas fa-comment-dots text-base"></i> แทรกไอเทมลงแชท
            <span class="tooltip">แทรกไอเทมลงแชท</span>
          </button>
          <button v-if="selectedItem?.source === 'storage'" @click="moveToBag"
            class="w-full py-3 px-4 bg-green-600 hover:bg-green-500 border border-green-700 text-white rounded-lg transition-all duration-300 flex items-center justify-center gap-2 shadow-md hover:shadow-xl transform hover:scale-105 relative"
            :class="{ 'opacity-60 cursor-not-allowed': isMoving }" :disabled="isMoving">
            <span v-if="!isMoving"><i class="fas fa-arrow-right text-base"></i> นำออกจากคลัง</span>
            <i v-else class="fas fa-spinner animate-spin text-indigo-200 text-base"></i>
            <span class="tooltip">นำออกจากคลัง</span>
          </button>
        </div>
        <div v-if="itemError" class="text-red-300 mt-4 text-sm">{{ itemError }}</div>
      </div>
    </div>
  </transition>

  <!-- Sort Modal -->
  <transition name="fade">
    <div v-if="showSortModal" class="fixed inset-0 bg-gray-900 bg-opacity-80 flex items-center justify-center z-50">
      <div class="bg-gray-800 p-6 rounded-lg border border-gray-700 shadow-xl w-full max-w-md">
        <div class="flex items-center gap-2 mb-4">
          <i class="fas fa-sort text-indigo-300 text-lg"></i>
          <h3 class="text-lg font-semibold text-indigo-300">กำหนดลำดับการจัดเรียง</h3>
        </div>
        <p class="text-sm text-gray-400 mb-4">ใช้ปุ่มเพื่อจัดลำดับประเภทไอเทม</p>
        <div class="max-h-64 overflow-y-auto mb-4 scrollbar-thin">
          <transition-group tag="ul" name="sort-transition" class="space-y-2">
            <li v-for="(type, index) in storageSortOrder" :key="type"
              class="flex items-center justify-between bg-gray-700 p-2 rounded-lg border border-gray-600 hover:bg-gray-600 transition-all duration-200">
              <button @click="moveUp(index)" class="p-1 text-gray-300 hover:text-white w-10 relative" :disabled="index === 0">
                <i class="fas fa-arrow-up"></i><span class="tooltip">เลื่อนขึ้น</span>
              </button>
              <span class="text-sm text-white flex-1 text-center">{{ type }}</span>
              <button @click="moveDown(index)" class="p-1 text-gray-300 hover:text-white w-10 relative" :disabled="index === storageSortOrder.length - 1">
                <i class="fas fa-arrow-down"></i><span class="tooltip">เลื่อนลง</span>
              </button>
            </li>
          </transition-group>
        </div>
        <div v-if="error" class="text-red-300 mb-4 text-sm">{{ error }}</div>
        <div class="flex justify-end space-x-2">
          <button @click="resetSortOrder" class="py-2 px-4 bg-yellow-600 hover:bg-yellow-500 border border-yellow-700 text-white rounded-lg transition-all duration-200 flex items-center gap-2 relative">
            <i class="fas fa-undo text-sm"></i> คืนค่าเริ่มต้น<span class="tooltip">คืนค่าเริ่มต้น</span>
          </button>
          <button @click="saveSortOrder"
            class="py-2 px-4 bg-indigo-600 hover:bg-indigo-500 border border-indigo-700 text-white rounded-lg transition-all duration-200 flex items-center gap-2 relative"
            :class="{ 'opacity-60 cursor-not-allowed': storageLoading }" :disabled="storageLoading">
            <span v-if="!storageLoading"><i class="fas fa-save text-sm"></i> บันทึกและจัดเรียง</span>
            <i v-else class="fas fa-spinner animate-spin text-indigo-200 text-sm"></i>
            <span class="tooltip">บันทึกและจัดเรียง</span>
          </button>
          <button @click="showSortModal = false" class="py-2 px-4 bg-gray-600 hover:bg-gray-500 border border-gray-600 text-white rounded-lg transition-all duration-200 flex items-center gap-2 relative">
            <i class="fas fa-times text-sm"></i> ยกเลิก<span class="tooltip">ยกเลิก</span>
          </button>
        </div>
      </div>
    </div>
  </transition>

  <!-- Detail Item Modal -->
  <detail-item-modal v-if="showDetailModal" :item="selectedItemForModal || {}" :itemId="selectedItemId" @close="showDetailModal = false" />
</template>

<script>
import { ref, onMounted, onUnmounted, watch, computed } from 'vue';
import { projectFirestore, timestamp, projectFunctions } from '@/firebase/config';
import getUser from '@/composables/getUser';
import { usePlayerStore } from '@/composables/usePlayerStore';
import { useInventoryStore } from '@/composables/useInventoryStore';
import gameItemsData from '@/data/gameItems.json';
import DetailItemModal from '@/components/DetailItemModal.vue';

export default {
  components: { DetailItemModal },
  setup() {
    const { user } = getUser();
    const messages = ref([]);
    const lastVisible = ref(null);
    const loadingMore = ref(false);
    const hasMoreMessages = ref(true);
    const newMessage = ref('');
    const chatRoom = ref('general');
    const loading = ref(true);
    const error = ref(null);
    const sending = ref(false);
    const showStorage = ref(false);
    const storageItems = ref([]);
    const storageLoading = ref(false);
    const selectedItem = ref(null);
    const itemError = ref(null);
    const showDetailModal = ref(false);
    const selectedItemId = ref(null);
    const selectedItemForModal = ref(null);
    const itemCache = ref({});
    const quoteCache = ref({});
    const searchQuery = ref('');
    const isMoving = ref(false);
    const bagItems = ref([]);
    const equippedItems = ref([]);
    const filteredStorageItems = ref([]);
    const inventorySource = ref('storage');
    const storageSortPreference = ref('type');
    const selectedType = ref('');
    const showTransferOnly = ref(false);
    const showSortModal = ref(false);
    const isAdmin = ref(false);
    const isBannedFromChat = ref(false);
    let characterData = ref(null);
    const playerStore = usePlayerStore();
    const inventoryStore = useInventoryStore();
    let unsubscribe = null;

    // ── Kill Feed ─────────────────────────────────────────────────
    const killFeedCharCache = ref({});

    const killFeedTheme = (cause) => {
      const themes = {
        assassination: {
          card:    'kill-feed-card--assassination',
          ribbon:  'kill-feed-ribbon--assassination',
          glowBar: 'kill-feed-glow-bar--assassination',
          icon: '🗡️', label: 'ลอบสังหาร',
          vsEmoji: '🗡️', vsIcon: 'kill-feed-vs--assassination',
        },
        // ผู้โจมตีตายจาก PvP ที่ survivor ใช้ลูกเต๋าโบนัสจากกับดัก
        trapAssassination: {
          card:    'kill-feed-card--trap',
          ribbon:  'kill-feed-ribbon--trap',
          glowBar: 'kill-feed-glow-bar--trap',
          icon: '⚙️', label: 'กับดัก + ลอบสังหาร',
          vsEmoji: '⚙️', vsIcon: 'kill-feed-vs--trap',
        },
        trap: {
          card:    'kill-feed-card--trap',
          ribbon:  'kill-feed-ribbon--trap',
          glowBar: 'kill-feed-glow-bar--trap',
          icon: '⚙️', label: 'กับดัก',
          vsEmoji: '⚙️', vsIcon: 'kill-feed-vs--trap',
        },
        bomb: {
          card:    'kill-feed-card--bomb',
          ribbon:  'kill-feed-ribbon--bomb',
          glowBar: 'kill-feed-glow-bar--bomb',
          icon: '💣', label: 'ระเบิด',
          vsEmoji: '💥', vsIcon: 'kill-feed-vs--bomb',
        },
        ghost: {
          card:    'kill-feed-card--ghost',
          ribbon:  'kill-feed-ribbon--ghost',
          glowBar: 'kill-feed-glow-bar--ghost',
          icon: '👻', label: 'วิญญาณเฝ้าห้อง',
          vsEmoji: '👻', vsIcon: 'kill-feed-vs--ghost',
        },
        robot: {
          card:    'kill-feed-card--robot',
          ribbon:  'kill-feed-ribbon--robot',
          glowBar: 'kill-feed-glow-bar--robot',
          icon: '🤖', label: 'หุ่นยนต์นักฆ่า',
          vsEmoji: '⚙️', vsIcon: 'kill-feed-vs--robot',
        },
      };
      return themes[cause] || themes.assassination;
    };

    const loadKillFeedChar = async (uid) => {
      if (!uid || killFeedCharCache.value[uid]) return;
      killFeedCharCache.value[uid] = { loading: true };
      try {
        const doc = await projectFirestore.collection('characters').doc(uid).get();
        killFeedCharCache.value[uid] = doc.exists
          ? { characterName: doc.data().characterName || 'ไม่ทราบชื่อ', imageUrl: doc.data().imageUrl || '' }
          : { error: true, characterName: 'ไม่ทราบชื่อ', imageUrl: '' };
      } catch {
        killFeedCharCache.value[uid] = { error: true, characterName: 'ไม่ทราบชื่อ', imageUrl: '' };
      }
    };

    const getKillFeedChar = (uid) => {
      if (!uid) return null;
      if (!killFeedCharCache.value[uid]) { loadKillFeedChar(uid); return null; }
      return killFeedCharCache.value[uid];
    };

    const loadKillFeedCharsFromMessages = (msgs) => {
      msgs.forEach((msg) => {
        if (msg.type !== 'killFeed') return;
        if (msg.killerUid)    loadKillFeedChar(msg.killerUid);
        if (msg.victimUid)    loadKillFeedChar(msg.victimUid);
        if (msg.trapOwnerUid) loadKillFeedChar(msg.trapOwnerUid); // ← fetch เจ้าของกับดักด้วย
      });
    };

    // ── Banned users ──────────────────────────────────────────────
    const bannedChatUsersRef = projectFirestore.collection('server').doc('bannedChatUsers');
    const unsubscribeBannedChatUsers = bannedChatUsersRef.onSnapshot((doc) => {
      isBannedFromChat.value = doc.exists && doc.data().uids && user.value
        ? doc.data().uids.includes(user.value.uid) : false;
    }, (err) => { console.error(err); });

    // ── Sort order ────────────────────────────────────────────────
    const defaultSortOrder = ['หมวก','แว่นตา','เสื้อ','เสื้อคลุม','เกราะ','ปลอกแขน','ถุงมือ','กางเกง','สนับแข้ง','รองเท้า','เข็มขัด','สร้อยคอ','แหวน','อาวุธ','อาวุธสำรอง','ตะกรุด','ผ้ายันต์','สายสิญจน์','อวัยวะจักรกล','หนังสือ','อาหาร','แผนที่','รักษา','กัปดัก','วัสดุ','ทั่วไป'];
    const storageSortOrder = ref([...defaultSortOrder]);
    const typeOptions = [...defaultSortOrder];

    const loadSortOrder = () => {
      try {
        const s = localStorage.getItem('sortOrder');
        if (s) {
          const p = JSON.parse(s);
          if (p.length === defaultSortOrder.length && defaultSortOrder.every(t => p.includes(t))) storageSortOrder.value = p;
        }
      } catch(e) { console.error(e); }
    };
    const saveSortOrderToLocal = () => {
      try { localStorage.setItem('sortOrder', JSON.stringify(storageSortOrder.value)); } catch(e) { console.error(e); }
    };
    const resetSortOrder = () => { storageSortOrder.value = [...defaultSortOrder]; saveSortOrderToLocal(); };
    const moveUp = (i) => { if (i === 0) return; const o = [...storageSortOrder.value]; [o[i-1], o[i]] = [o[i], o[i-1]]; storageSortOrder.value = o; };
    const moveDown = (i) => { if (i === storageSortOrder.value.length - 1) return; const o = [...storageSortOrder.value]; [o[i], o[i+1]] = [o[i+1], o[i]]; storageSortOrder.value = o; };
    const saveSortOrder = () => { saveSortOrderToLocal(); showSortModal.value = false; updateFilteredStorageItems(); };
    const openSortModal = () => { showSortModal.value = true; error.value = ''; };
    const toggleTransferFilter = () => { showTransferOnly.value = !showTransferOnly.value; updateFilteredStorageItems(); };

    const updateFilteredStorageItems = () => {
      let sourceItems;
      if (inventorySource.value === 'bag') sourceItems = bagItems.value;
      else if (inventorySource.value === 'equipped') sourceItems = equippedItems.value;
      else sourceItems = storageItems.value;
      let items = [...sourceItems];
      if (storageSortPreference.value === 'type') {
        const order = storageSortOrder.value.reduce((acc, t, i) => { acc[t] = i + 1; return acc; }, {});
        items.sort((a, b) => {
          const oa = order[a.type] || 999, ob = order[b.type] || 999;
          if (oa !== ob) return oa - ob;
          const ra = a.rarity ? String(a.rarity) : '', rb = b.rarity ? String(b.rarity) : '';
          if (ra !== rb) return ra.localeCompare(rb, 'th');
          return a.name.localeCompare(b.name, 'th');
        });
      }
      filteredStorageItems.value = items.filter(item => {
        const t = selectedType.value ? item.type === selectedType.value : true;
        const s = searchQuery.value ? item.name.toLowerCase().includes(searchQuery.value.toLowerCase()) : true;
        const x = showTransferOnly.value ? item.transfer === true : true;
        return t && s && x;
      });
    };

    watch(() => playerStore.characterData, (data) => {
      if (data) characterData.value = data;
    }, { immediate: true });

    watch(() => playerStore.userData, (data) => {
      if (data) isAdmin.value = data.admin === true;
    }, { immediate: true });

    const subscribeToMessages = () => {
      if (unsubscribe) unsubscribe();
      const query = projectFirestore.collection('chats').doc(chatRoom.value).collection('messages').orderBy('timestamp', 'desc').limit(30);
      unsubscribe = query.onSnapshot((snap) => {
        messages.value = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        lastVisible.value = snap.docs[snap.docs.length - 1];
        hasMoreMessages.value = snap.docs.length === 30;
        loading.value = false;
        loadItems(extractUniqueItemIds(messages.value), getEmbeddedItemsFromMessages(messages.value));
        loadQuotes(extractUniqueQuoteIds(messages.value));
        loadKillFeedCharsFromMessages(messages.value);
      }, (err) => { error.value = err.message; loading.value = false; });
    };

    const loadMore = async () => {
      if (!lastVisible.value || loadingMore.value) return;
      loadingMore.value = true;
      try {
        const snap = await projectFirestore.collection('chats').doc(chatRoom.value).collection('messages').orderBy('timestamp', 'desc').startAfter(lastVisible.value).limit(10).get();
        const newMsgs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        messages.value = [...messages.value, ...newMsgs];
        lastVisible.value = snap.docs[snap.docs.length - 1];
        hasMoreMessages.value = snap.docs.length === 10;
        loadItems(extractUniqueItemIds(newMsgs), getEmbeddedItemsFromMessages(newMsgs));
        loadQuotes(extractUniqueQuoteIds(newMsgs));
        loadKillFeedCharsFromMessages(newMsgs);
      } catch(err) { error.value = err.message; }
      finally { loadingMore.value = false; }
    };

    const sendMessage = async () => {
      if (!newMessage.value.trim() || !user.value || !characterData.value || isBannedFromChat.value) return;
      sending.value = true;
      try {
        const messageText = newMessage.value.trim();
        // ดึง instanceId ของไอเทมจากข้อความ แล้วฝัง { templateId, states } ตามหลัก migration
        const itemIds = extractUniqueItemIds([{ message: messageText }]);
        const itemsData = {};
        const rawStorage = inventoryStore.storageData?.items || {};
        const rawBag = inventoryStore.bagData?.items || {};
        const charPrivate = playerStore.characterPrivateData || {};
        const rawEquipped = charPrivate.equippedItems || {};
        const rawMechanical = charPrivate.mechanicalItems || {};
        const rawGhost = charPrivate.ghostItems || {};
        for (const id of itemIds) {
          const rawItem = rawStorage[id] || rawBag[id] || rawEquipped[id] || rawMechanical[id] || rawGhost[id];
          if (rawItem && rawItem.templateId) {
            itemsData[id] = { templateId: rawItem.templateId, states: rawItem.states || {} };
          }
        }
        const payload = {
          senderUID: user.value.uid,
          senderName: characterData.value.characterName || 'Unknown',
          senderImage: characterData.value.imageUrl || '',
          message: messageText,
          timestamp: timestamp(),
        };
        if (Object.keys(itemsData).length > 0) payload.items = itemsData;
        await projectFirestore.collection('chats').doc(chatRoom.value).collection('messages').add(payload);
        newMessage.value = '';
      } catch(err) { error.value = err.message; }
      finally { sending.value = false; }
    };

    const deleteMessage = async (id) => {
      try { await projectFirestore.collection('chats').doc(chatRoom.value).collection('messages').doc(id).delete(); }
      catch(err) { error.value = err.message; }
    };

    const formatTimestamp = (ts) => {
      if (!ts) return '';
      return ts.toDate().toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' });
    };

    const extractUniqueItemIds = (msgs) => {
      const ids = new Set();
      msgs.forEach(m => { for (const match of (m.message || '').matchAll(/\[(item|ไอเทม):(\w+)\]/g)) ids.add(match[2]); });
      return [...ids];
    };
    const extractUniqueQuoteIds = (msgs) => {
      const ids = new Set();
      msgs.forEach(m => { for (const match of (m.message || '').matchAll(/\[quote:(\w+)\]/g)) ids.add(match[1]); });
      return [...ids];
    };

    const getEmbeddedItemsFromMessages = (msgs) => {
      return msgs.reduce((acc, m) => {
        if (m.items && typeof m.items === 'object') Object.assign(acc, m.items);
        return acc;
      }, {});
    };

    const loadItems = async (ids, embeddedItems = {}) => {
      for (const id of ids) {
        if (itemCache.value[id]) continue;
        itemCache.value[id] = { loading: true, error: false };
        try {
          // 1. ใช้ embedded data จากข้อความ (templateId + states) ตามหลัก migration
          const embedded = embeddedItems[id];
          if (embedded && embedded.templateId) {
            const tpl = gameItemsData[embedded.templateId];
            if (tpl && tpl.name) {
              const states = embedded.states || {};
              itemCache.value[id] = { loading: false, error: false, ...tpl, ...states };
              continue;
            }
          }
          // 2. Fallback: ค้นหาโดยตรงใน gameItemsData (สำหรับข้อความเก่าที่ไม่มี embedded data)
          const tpl = gameItemsData[id];
          if (tpl && tpl.name) {
            itemCache.value[id] = { name: tpl.name, img: tpl.img || 'default-item.png', loading: false, error: false };
            continue;
          }
          // 3. Final fallback: collection items เก่า
          const doc = await projectFirestore.collection('items').doc(id).get();
          itemCache.value[id] = doc.exists
            ? { name: doc.data().name || 'Unknown', img: doc.data().img || 'default-item.png', loading: false, error: false }
            : { loading: false, error: true };
        } catch { itemCache.value[id] = { loading: false, error: true }; }
      }
    };
    const loadQuotes = async (ids) => {
      for (const id of ids) {
        if (quoteCache.value[id]) continue;
        quoteCache.value[id] = { loading: true, error: false };
        try {
          const doc = await projectFirestore.collection('chats').doc(chatRoom.value).collection('messages').doc(id).get();
          quoteCache.value[id] = doc.exists
            ? { senderName: doc.data().senderName || 'Unknown', message: doc.data().message || '', loading: false, error: false }
            : { loading: false, error: true };
        } catch { quoteCache.value[id] = { loading: false, error: true }; }
      }
    };

    const parseMessage = (text) => {
      const parts = [];
      const regex = /\[(item|ไอเทม|quote):(\w+)\]/g;
      let lastIndex = 0, match;
      while ((match = regex.exec(text)) !== null) {
        if (match.index > lastIndex) parts.push({ type: 'text', content: text.slice(lastIndex, match.index) });
        parts.push({ type: match[1] === 'quote' ? 'quote' : 'item', id: match[2] });
        lastIndex = regex.lastIndex;
      }
      if (lastIndex < text.length) parts.push({ type: 'text', content: text.slice(lastIndex) });
      return parts;
    };
    const formatQuoteMessage = (m) => m;
    const openItemModal = (id) => {
      selectedItemId.value = id;
      const cached = itemCache.value[id];
      if (cached && cached.name && !cached.error && !cached.loading) {
        selectedItemForModal.value = { ...cached };
      } else {
        selectedItemForModal.value = gameItemsData[id] || null;
      }
      showDetailModal.value = true;
    };
    const quoteMessage = (msg) => { if (isBannedFromChat.value) return; newMessage.value = `[quote:${msg.id}] \n` + newMessage.value; };

    const toggleStorage = () => {
      showStorage.value = !showStorage.value;
      if (showStorage.value) { loadSortOrder(); }
    };

    const mapInventoryItems = (itemsObj, source) => {
      return Object.entries(itemsObj || {}).map(([instanceId, itemData]) => {
        const template = gameItemsData[itemData.templateId] || {};
        return {
          id: instanceId,
          source,
          templateId: itemData.templateId || '',
          count: itemData.states?.quantity || itemData.quantity || 1,
          name: template.name || itemData.templateId,
          img: template.img || '',
          type: template.type || '',
          rarity: template.rarity || '',
          rarityColors: template.rarityColors || {},
          weight: template.weight || 0,
          transfer: itemData.states?.transfer ?? template.transfer ?? false,
          ...template,
          ...itemData.states,
        };
      }).filter(item => item.templateId || gameItemsData[item.name]);
    };

    watch(() => inventoryStore.storageData, (data) => {
      if (!data) {
        storageItems.value = [];
        updateFilteredStorageItems();
        storageLoading.value = false;
        return;
      }
      storageLoading.value = true;
      const items = mapInventoryItems(data.items, 'storage').sort((a, b) => a.name.localeCompare(b.name));
      storageItems.value = items;
      updateFilteredStorageItems();
      storageLoading.value = false;
    }, { immediate: true });

    watch(() => inventoryStore.bagData, (data) => {
      if (!data) {
        bagItems.value = [];
        updateFilteredStorageItems();
        return;
      }
      const items = mapInventoryItems(data.items, 'bag').sort((a, b) => a.name.localeCompare(b.name));
      bagItems.value = items;
      updateFilteredStorageItems();
    }, { immediate: true });

    watch(() => playerStore.characterPrivateData, (data) => {
      if (!data) {
        equippedItems.value = [];
        updateFilteredStorageItems();
        return;
      }
      const rawEquipped = data.equippedItems || {};
      const rawMechanical = data.mechanicalItems || {};
      const rawGhost = data.ghostItems || {};
      const items = [
        ...mapInventoryItems(rawEquipped, 'equipped'),
        ...mapInventoryItems(rawMechanical, 'equipped'),
        ...mapInventoryItems(rawGhost, 'equipped'),
      ].sort((a, b) => a.name.localeCompare(b.name));
      equippedItems.value = items;
      updateFilteredStorageItems();
    }, { immediate: true });

    const selectItem = (item) => { selectedItem.value = item; itemError.value = null; };
    const insertItemToChat = () => {
      if (!selectedItem.value || isBannedFromChat.value) return;
      newMessage.value += `[ไอเทม:${selectedItem.value.id}] `;
      selectedItem.value = null; showStorage.value = false;
    };
    const moveToBag = async () => {
      if (!user.value || !selectedItem.value) return;
      isMoving.value = true;
      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        await gameActions({ action: 'moveItemToBag', instanceId: selectedItem.value.id });
        selectedItem.value = null;
      }
      catch(err) { itemError.value = err.message || 'เกิดข้อผิดพลาดในการย้ายไอเทมไปกระเป๋า'; }
      finally { isMoving.value = false; }
    };
    const handleImageError = (e) => { e.target.src = 'default-item.png'; };

    const filteredMessages = computed(() => messages.value);

    onMounted(() => {
      if (user.value?.uid) {
        playerStore.init(user.value.uid);
      }
      subscribeToMessages();
    });
    onUnmounted(() => {
      if (unsubscribe) unsubscribe();
      unsubscribeBannedChatUsers();
    });
    watch(chatRoom, () => { loading.value = true; messages.value = []; lastVisible.value = null; hasMoreMessages.value = true; subscribeToMessages(); });
    watch(messages, () => {
      loadItems(extractUniqueItemIds(messages.value), getEmbeddedItemsFromMessages(messages.value));
      loadQuotes(extractUniqueQuoteIds(messages.value));
      loadKillFeedCharsFromMessages(messages.value);
    }, { deep: true });
    watch([searchQuery, selectedType, storageSortPreference, storageSortOrder, inventorySource], updateFilteredStorageItems);

    return {
      user, messages, newMessage, chatRoom, loading, error, sending,
      showStorage, storageItems, bagItems, equippedItems, storageLoading, selectedItem, itemError, inventorySource,
      showDetailModal, selectedItemId, selectedItemForModal, itemCache, quoteCache, searchQuery,
      filteredStorageItems, isMoving, loadingMore, hasMoreMessages,
      sendMessage, deleteMessage, formatTimestamp, parseMessage,
      openItemModal, toggleStorage, selectItem, insertItemToChat,
      moveToBag, handleImageError, loadMore, storageSortPreference,
      selectedType, showTransferOnly, showSortModal, storageSortOrder, inventorySource, equippedItems,
      typeOptions, toggleTransferFilter, openSortModal, moveUp, moveDown,
      saveSortOrder, resetSortOrder, isAdmin, filteredMessages,
      quoteMessage, formatQuoteMessage, isBannedFromChat,
      killFeedCharCache, getKillFeedChar, killFeedTheme,
    };
  },
};
</script>

<style scoped>
/* ─── Scrollbar ─────────────────────────────────────────────────── */
.scrollbar-thin::-webkit-scrollbar { width: 5px; }
.scrollbar-thin::-webkit-scrollbar-track { background: #2d3748; border-radius: 10px; }
.scrollbar-thin::-webkit-scrollbar-thumb { background: #4a5568; border-radius: 10px; }
.scrollbar-thin::-webkit-scrollbar-thumb:hover { background: #6b7280; }
.scrollbar-thin { scrollbar-width: thin; scrollbar-color: #4a5568 #2d3748; }

/* ─── Fade ───────────────────────────────────────────────────────── */
.fade-enter-active, .fade-leave-active { transition: opacity 0.2s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

/* ─── FadeIn ─────────────────────────────────────────────────────── */
@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
.animate-fadeIn { animation: fadeInUp 0.3s ease-out both; }

/* ─── Background ─────────────────────────────────────────────────── */
.bg {
  background:
    url('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/bg%2Frelaxroom.png?alt=media&token=2bc4f562-7fe7-4126-b7fb-71ada3aa25a2') no-repeat center center fixed,
    linear-gradient(to bottom, rgba(31,41,55,0.8), rgba(0,0,0,0.9));
  background-size: cover;
}

/* ─── Tooltip ────────────────────────────────────────────────────── */
.tooltip {
  visibility: hidden; opacity: 0;
  position: absolute; bottom: 100%; left: 50%; transform: translateX(-50%);
  background: #1f2937; color: #e0e7ff; padding: 0.4rem 0.8rem;
  border-radius: 0.375rem; font-size: 0.7rem; white-space: nowrap; z-index: 70;
  transition: opacity 0.2s ease, visibility 0.2s ease; margin-bottom: 0.4rem;
  pointer-events: none;
}
button:hover .tooltip, i:hover + .tooltip { visibility: visible; opacity: 1; }

/* ─── Sort transition ────────────────────────────────────────────── */
.sort-transition-move { transition: transform 0.3s ease; }
.sort-transition-enter-active, .sort-transition-leave-active { transition: all 0.3s ease; }
.sort-transition-enter-from, .sort-transition-leave-to { opacity: 0; transform: translateY(20px); }

/* ══════════════════════════════════════════════════════════════════
   KILL FEED — base
══════════════════════════════════════════════════════════════════ */
.kill-feed-wrapper { display: flex; justify-content: center; padding: 2px 0; }

.kill-feed-card {
  position: relative;
  width: 100%;
  max-width: 520px;
  border-radius: 12px;
  overflow: hidden;
  border-width: 1px;
  border-style: solid;
}

/* scanline */
@keyframes scanline {
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}
.kill-feed-scanline {
  position: absolute; top: 0; left: 0; right: 0; bottom: 0;
  background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.04) 50%, transparent 100%);
  animation: scanline 3s linear infinite;
  pointer-events: none; z-index: 1;
}

/* ribbon */
.kill-feed-ribbon {
  display: flex; align-items: center; gap: 6px; padding: 5px 10px;
  font-size: 0.65rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase;
  border-bottom-width: 1px; border-bottom-style: solid;
}
.kill-feed-ribbon-icon { font-size: 0.85rem; }
.kill-feed-ribbon-label { flex: 1; }
.kill-feed-ribbon-time { color: rgba(255,255,255,0.4); font-size: 0.6rem; font-weight: 400; letter-spacing: 0; text-transform: none; }

/* standard 2-column combat row */
.kill-feed-combat {
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 14px; gap: 8px;
}

/* ─── 3-column trap layout ────────────────────────────────────── */
.kill-feed-triple {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 10px;
  gap: 4px;
}
/* make each column equal width in triple */
.kill-feed-triple .kill-feed-combatant { flex: 1; }
.kill-feed-triple .kill-feed-vs-col   { flex-shrink: 0; width: 44px; }

/* combatant column */
.kill-feed-combatant {
  display: flex; flex-direction: column; align-items: center; gap: 4px;
  flex: 1; min-width: 0;
}

/* avatar */
.kill-feed-avatar-wrap { position: relative; flex-shrink: 0; }
.kill-feed-avatar {
  width: 44px; height: 44px; border-radius: 50%; object-fit: cover;
  border-width: 2px; border-style: solid; display: block;
}
.kill-feed-avatar-badge {
  position: absolute; bottom: -4px; right: -4px; font-size: 0.75rem; line-height: 1;
}

/* victim — grayscale dim */
.victim .kill-feed-avatar { filter: grayscale(60%) brightness(0.75); }

/* name / role */
.kill-feed-name {
  font-size: 0.75rem; font-weight: 700; text-align: center;
  max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; line-height: 1.2;
}
.kill-feed-role {
  font-size: 0.55rem; font-weight: 600; letter-spacing: 0.06em;
  text-transform: uppercase; opacity: 0.55;
}

/* vs centre column */
.kill-feed-vs-col {
  display: flex; flex-direction: column; align-items: center; gap: 4px;
  flex-shrink: 0; width: 52px;
}
.kill-feed-vs-icon { font-size: 1.4rem; line-height: 1; filter: drop-shadow(0 0 4px currentColor); }
.kill-feed-weapon {
  font-size: 0.55rem; text-align: center; color: rgba(255,255,255,0.45);
  max-width: 60px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}

/* glow bar */
.kill-feed-glow-bar { height: 2px; width: 100%; }

/* ─── Trap Owner column extras ───────────────────────────────── */
/* bright border to distinguish from victim */
.kill-feed-avatar-wrap.trap-owner .kill-feed-avatar {
  filter: none;
  border-color: #eab308;   /* yellow — matches trap theme */
}

/* dice bonus badge — small pill below avatar */
@keyframes dicePop {
  0%   { transform: scale(0.7); opacity: 0; }
  60%  { transform: scale(1.15); opacity: 1; }
  100% { transform: scale(1); }
}
.trap-dice-bonus {
  display: flex; align-items: center; gap: 2px;
  background: rgba(234,179,8,0.18);
  border: 1px solid rgba(234,179,8,0.5);
  border-radius: 999px;
  padding: 1px 6px;
  animation: dicePop 0.4s ease-out 0.3s both;
}
.trap-dice-bonus-icon { font-size: 0.7rem; }
.trap-dice-bonus-text { font-size: 0.55rem; font-weight: 700; color: #fde68a; letter-spacing: 0.04em; }

.trap-owner-name { color: #fde68a; text-shadow: 0 0 8px rgba(234,179,8,0.5); }
.trap-owner-role { color: #fbbf24; }
.trap-owner-badge { bottom: -4px; right: -4px; font-size: 0.75rem; }

/* ══════════════════════════════════════════════════════════════════
   KILL FEED — per-cause themes
══════════════════════════════════════════════════════════════════ */

/* ── ASSASSINATION ── */
@keyframes glowAssassination {
  0%,100% { box-shadow: 0 0 6px 1px rgba(185,28,28,0.3),  inset 0 0 20px rgba(185,28,28,0.05); }
  50%     { box-shadow: 0 0 16px 3px rgba(185,28,28,0.55), inset 0 0 30px rgba(185,28,28,0.1); }
}
.kill-feed-card--assassination {
  background: linear-gradient(135deg, #0f0505 0%, #1c0a0a 50%, #0f0505 100%);
  border-color: #7f1d1d;
  animation: fadeInUp 0.4s ease-out both, glowAssassination 4s ease-in-out infinite;
}
.kill-feed-ribbon--assassination {
  background: linear-gradient(90deg, rgba(127,29,29,0.8) 0%, rgba(185,28,28,0.4) 100%);
  border-bottom-color: rgba(127,29,29,0.6); color: #fca5a5;
}
.kill-feed-card--assassination .kill-feed-avatar-wrap.killer .kill-feed-avatar { border-color: #ef4444; }
.kill-feed-card--assassination .kill-feed-avatar-wrap.victim  .kill-feed-avatar { border-color: #7f1d1d; }
.kill-feed-card--assassination .killer-name { color: #fca5a5; text-shadow: 0 0 8px rgba(239,68,68,0.5); }
.kill-feed-card--assassination .victim-name  { color: #6b7280; text-decoration: line-through; text-decoration-color: #ef4444; }
.kill-feed-card--assassination .killer-role  { color: #f87171; }
.kill-feed-card--assassination .victim-role  { color: #4b5563; }
.kill-feed-vs--assassination { color: #ef4444; }
.kill-feed-glow-bar--assassination { background: linear-gradient(90deg, transparent, #ef4444, transparent); }

/* ── TRAP ── */
@keyframes glowTrap {
  0%,100% { box-shadow: 0 0 6px 1px rgba(161,98,7,0.3),   inset 0 0 20px rgba(161,98,7,0.05); }
  50%     { box-shadow: 0 0 16px 3px rgba(234,179,8,0.55), inset 0 0 30px rgba(234,179,8,0.1); }
}
.kill-feed-card--trap {
  background: linear-gradient(135deg, #0c0a00 0%, #1a1300 50%, #0c0a00 100%);
  border-color: #78350f;
  animation: fadeInUp 0.4s ease-out both, glowTrap 4s ease-in-out infinite;
}
.kill-feed-ribbon--trap {
  background: linear-gradient(90deg, rgba(120,53,15,0.8) 0%, rgba(161,98,7,0.4) 100%);
  border-bottom-color: rgba(120,53,15,0.6); color: #fde68a;
}
/* victim border in trap layout */
.kill-feed-card--trap .kill-feed-avatar-wrap.victim .kill-feed-avatar { border-color: #78350f; }
.kill-feed-card--trap .victim-name  { color: #6b7280; text-decoration: line-through; text-decoration-color: #eab308; }
.kill-feed-card--trap .victim-role  { color: #4b5563; }
.kill-feed-vs--trap { color: #eab308; }
.kill-feed-glow-bar--trap { background: linear-gradient(90deg, transparent, #eab308, transparent); }

/* ── BOMB ── */
@keyframes glowBomb {
  0%,100% { box-shadow: 0 0 6px 1px rgba(154,52,18,0.3),   inset 0 0 20px rgba(154,52,18,0.05); }
  50%     { box-shadow: 0 0 18px 4px rgba(251,146,60,0.55), inset 0 0 30px rgba(251,146,60,0.1); }
}
.kill-feed-card--bomb {
  background: linear-gradient(135deg, #0d0500 0%, #1a0b00 50%, #0d0500 100%);
  border-color: #9a3412;
  animation: fadeInUp 0.4s ease-out both, glowBomb 4s ease-in-out infinite;
}
.kill-feed-ribbon--bomb {
  background: linear-gradient(90deg, rgba(154,52,18,0.8) 0%, rgba(194,65,12,0.4) 100%);
  border-bottom-color: rgba(154,52,18,0.6); color: #fed7aa;
}
.kill-feed-card--bomb .kill-feed-avatar-wrap.killer .kill-feed-avatar { border-color: #f97316; }
.kill-feed-card--bomb .kill-feed-avatar-wrap.victim  .kill-feed-avatar { border-color: #9a3412; }
.kill-feed-card--bomb .killer-name { color: #fed7aa; text-shadow: 0 0 8px rgba(249,115,22,0.5); }
.kill-feed-card--bomb .victim-name  { color: #6b7280; text-decoration: line-through; text-decoration-color: #f97316; }
.kill-feed-card--bomb .killer-role  { color: #fb923c; }
.kill-feed-card--bomb .victim-role  { color: #4b5563; }
.kill-feed-vs--bomb { color: #f97316; }
.kill-feed-glow-bar--bomb { background: linear-gradient(90deg, transparent, #f97316, transparent); }

/* ── GHOST ── */
@keyframes glowGhost {
  0%,100% { box-shadow: 0 0 6px 1px rgba(88,28,135,0.3),   inset 0 0 20px rgba(88,28,135,0.05); }
  50%     { box-shadow: 0 0 16px 3px rgba(168,85,247,0.55), inset 0 0 30px rgba(168,85,247,0.1); }
}
.kill-feed-card--ghost {
  background: linear-gradient(135deg, #060010 0%, #0e0020 50%, #060010 100%);
  border-color: #581c87;
  animation: fadeInUp 0.4s ease-out both, glowGhost 4s ease-in-out infinite;
}
.kill-feed-ribbon--ghost {
  background: linear-gradient(90deg, rgba(88,28,135,0.8) 0%, rgba(126,34,206,0.4) 100%);
  border-bottom-color: rgba(88,28,135,0.6); color: #e9d5ff;
}
.kill-feed-card--ghost .kill-feed-avatar-wrap.killer .kill-feed-avatar { border-color: #a855f7; }
.kill-feed-card--ghost .kill-feed-avatar-wrap.victim  .kill-feed-avatar { border-color: #581c87; }
.kill-feed-card--ghost .killer-name { color: #e9d5ff; text-shadow: 0 0 8px rgba(168,85,247,0.5); }
.kill-feed-card--ghost .victim-name  { color: #6b7280; text-decoration: line-through; text-decoration-color: #a855f7; }
.kill-feed-card--ghost .killer-role  { color: #c084fc; }
.kill-feed-card--ghost .victim-role  { color: #4b5563; }
.kill-feed-vs--ghost { color: #a855f7; }
.kill-feed-glow-bar--ghost { background: linear-gradient(90deg, transparent, #a855f7, transparent); }

/* ─── Robot ───────────────────────────────────────────────────────── */
@keyframes glowRobot { 0%,100%{box-shadow:0 0 8px rgba(6,182,212,0.3),inset 0 0 8px rgba(6,182,212,0.05)} 50%{box-shadow:0 0 18px rgba(6,182,212,0.6),inset 0 0 14px rgba(6,182,212,0.1)} }
.kill-feed-card--robot {
  background: linear-gradient(135deg, #00101a 0%, #001e2e 50%, #00101a 100%);
  border-color: #155e75;
  animation: fadeInUp 0.4s ease-out both, glowRobot 4s ease-in-out infinite;
}
.kill-feed-ribbon--robot {
  background: linear-gradient(90deg, rgba(21,94,117,0.8) 0%, rgba(6,182,212,0.4) 100%);
  border-bottom-color: rgba(21,94,117,0.6); color: #cffafe;
}
.kill-feed-card--robot .kill-feed-avatar-wrap.killer .kill-feed-avatar { border-color: #06b6d4; }
.kill-feed-card--robot .kill-feed-avatar-wrap.victim  .kill-feed-avatar { border-color: #155e75; }
.kill-feed-card--robot .killer-name { color: #cffafe; text-shadow: 0 0 8px rgba(6,182,212,0.5); }
.kill-feed-card--robot .victim-name  { color: #6b7280; text-decoration: line-through; text-decoration-color: #06b6d4; }
.kill-feed-card--robot .killer-role  { color: #67e8f9; }
.kill-feed-card--robot .victim-role  { color: #4b5563; }
.kill-feed-vs--robot { color: #06b6d4; }
.kill-feed-glow-bar--robot { background: linear-gradient(90deg, transparent, #06b6d4, transparent); }

/* ─── Mobile ─────────────────────────────────────────────────────── */
@media (max-width: 639px) {
  .kill-feed-avatar { width: 34px; height: 34px; }
  .kill-feed-name { font-size: 0.6rem; }
  .kill-feed-vs-col { width: 36px; }
  .kill-feed-triple .kill-feed-vs-col { width: 32px; }
  .kill-feed-vs-icon { font-size: 1rem; }
  .trap-dice-bonus-text { font-size: 0.5rem; }
  button:hover .tooltip, i:hover + .tooltip { visibility: hidden; opacity: 0; }
}
</style>